import type { ImportActionModuleScoresRequest } from '@/api/model'
import { IncentivationDataType } from '@/api/model/incentivationDataType'
import type { SheetRow } from '@/lib/readSpreadsheet'

// Excel column headers — data contract with the customer's template, NOT translatable UI.
// Kept identical to the old CustomerPortal so existing files keep importing.
const COL_EMPLOYEE_NUMBER = 'EmployeeNumber'
const COL_SCORE = 'Score'
const COL_REWARD = 'Reward'
const COL_IS_FULFILLED = 'IsFulfilled'
const RESERVED_COLS = [COL_EMPLOYEE_NUMBER, COL_SCORE, COL_REWARD, COL_IS_FULFILLED]

type ExcelRow = SheetRow

/** Every non-reserved column becomes a subtype-data entry (values stringified). */
function subTypeData(row: ExcelRow): Record<string, string> {
  const entries: Record<string, string> = {}
  for (const [key, value] of Object.entries(row)) {
    if (RESERVED_COLS.includes(key) || value === undefined || value === null) continue
    entries[key] = String(value)
  }
  return entries
}

/**
 * Maps parsed Excel rows to the import payload, mirroring the old CustomerPortal:
 * the module's expected incentivation data type decides which column feeds the reward
 * (Score → Score, Reward → ManualInput, IsFulfilled → Binary). Rows without an employee
 * number are dropped. Modules whose data type can't be imported yield an empty result.
 */
export function parseIncentivationExcel(
  rows: ExcelRow[],
  dataType: IncentivationDataType,
): ImportActionModuleScoresRequest[] {
  const result: ImportActionModuleScoresRequest[] = []

  for (const row of rows) {
    const employeeNumber = row[COL_EMPLOYEE_NUMBER]
    if (employeeNumber === undefined || employeeNumber === null || String(employeeNumber).trim() === '') continue
    const subTypes = subTypeData(row)

    switch (dataType) {
      case IncentivationDataType.Binary:
        result.push({
          employeeNumber: String(employeeNumber),
          rewardCalculationInformation: {
            incentivationDataType: IncentivationDataType.Binary,
            // Any non-empty value in the IsFulfilled column counts as fulfilled.
            isFulfilled: String(row[COL_IS_FULFILLED] ?? '').trim().length >= 1,
          },
          subTypeData: subTypes,
        })
        break
      case IncentivationDataType.Score:
        result.push({
          employeeNumber: String(employeeNumber),
          rewardCalculationInformation: {
            incentivationDataType: IncentivationDataType.Score,
            score: Number(row[COL_SCORE]),
          },
          subTypeData: subTypes,
        })
        break
      case IncentivationDataType.ManualInput:
        result.push({
          employeeNumber: String(employeeNumber),
          rewardCalculationInformation: {
            incentivationDataType: IncentivationDataType.ManualInput,
            enteredReward: Math.floor(Number(row[COL_REWARD])),
          },
          subTypeData: subTypes,
        })
        break
      default:
        // None / unknown — nothing importable.
        break
    }
  }

  return result
}
